import { useEffect, useState, type ChangeEvent } from 'react';
import { Button, Card, Field, Switch } from '../../components/kit';
import { RichTextEditor } from '../../components/RichTextEditor';
import { adminApi, type Contract } from '../../api/admin';
import { ApiRequestError } from '../../api/client';
import { DEFAULT_BRANDING, FONT_OPTIONS } from '../../branding';
import { useBranding } from '../../branding/BrandingProvider';
import '../page.css';
import './admin.css';

/** Genel site/marka ayarları — logo, uygulama adı, sekme başlığı, font, sunucu IP. */
function SiteForm() {
  const { refresh } = useBranding();
  const [v, setV] = useState<Record<string, any>>({ ...DEFAULT_BRANDING });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.getSetting('site').then((r) => setV((s) => ({ ...s, ...r.value })));
  }, []);

  const set = (k: string) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setV((s) => ({ ...s, [k]: e.target.value }));

  const onLogo = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr('');
    if (!file.type.startsWith('image/')) { setErr('Lütfen bir görsel dosyası seçin.'); return; }
    if (file.size > 1_000_000) { setErr('Logo en fazla ~1MB olmalı. Daha küçük bir görsel seçin.'); return; }
    const reader = new FileReader();
    reader.onload = () => setV((s) => ({ ...s, logo: String(reader.result) }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setErr('');
    setBusy(true);
    try {
      await adminApi.saveSetting('site', v);
      await refresh(); // tüm uygulamaya anında yansıt
      setMsg('Kaydedildi');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : 'Kaydedilemedi');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Genel / Marka" className="bank-card">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {err && <div className="auth-error">{err}</div>}

        <div className="site-logo-row">
          <span className={`brand-logo${v.logo ? ' brand-logo-img' : ''} logo-preview`}>
            {v.logo ? <img src={v.logo} alt="logo" /> : (v.appName || 'P').trim().charAt(0).toUpperCase() || 'P'}
          </span>
          <div className="site-logo-actions">
            <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
              Logo Yükle
              <input type="file" accept="image/*" onChange={onLogo} style={{ display: 'none' }} />
            </label>
            {v.logo && (
              <Button size="sm" variant="ghost" onClick={() => setV((s) => ({ ...s, logo: '' }))}>Kaldır</Button>
            )}
            <span className="pay-note">PNG/SVG önerilir, en fazla ~1MB. Kenar çubuğu ve giriş ekranında görünür.</span>
          </div>
        </div>

        <div className="inline-form">
          <Field label="Uygulama Adı" id="appName" value={v.appName ?? ''} onChange={set('appName')} />
          <Field label="Sekme Başlığı (browser title)" id="siteTitle" value={v.siteTitle ?? ''} onChange={set('siteTitle')} />
        </div>

        <div className="inline-form">
          <div className="field">
            <label>Yazı Tipi (font)</label>
            <select className="input" value={v.font ?? 'system'} onChange={set('font')}>
              {FONT_OPTIONS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
          <Field label="Yayın Adresi (Domain)" id="domain" value={v.domain ?? ''} onChange={set('domain')} placeholder="ör. tahsilat.persas.com.tr" />
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Button size="sm" onClick={save} disabled={busy}>{busy ? 'Kaydediliyor…' : 'Kaydet'}</Button>
          {msg && <span style={{ color: 'var(--good)', fontSize: 13 }}>{msg}</span>}
        </div>
      </div>
    </Card>
  );
}

function ContractEditor({ c, onSaved }: { c: Contract; onSaved: () => void }) {
  const [title, setTitle] = useState(c.title);
  const [body, setBody] = useState(c.body ?? '');
  const [msg, setMsg] = useState('');
  const save = async () => {
    await adminApi.saveContract(c.key, title, body);
    setMsg('Kaydedildi');
    onSaved();
    setTimeout(() => setMsg(''), 2000);
  };
  return (
    <Card className="bank-card">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Field label="Başlık" id={`t-${c.key}`} value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="field">
          <label>İçerik</label>
          <RichTextEditor value={body} onChange={setBody} />
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Button size="sm" onClick={save}>Kaydet</Button>
          {msg && <span style={{ color: 'var(--good)', fontSize: 13 }}>{msg}</span>}
        </div>
      </div>
    </Card>
  );
}

/** Veritabanı bağlantı ayarları — config/database.json'a yazar (yeniden başlatınca etkinleşir). */
function DbSettingsForm() {
  const [v, setV] = useState<Record<string, any>>({ host: '', port: 3306, database: '', user: '', password: '', socketPath: '' });
  const [source, setSource] = useState<'file' | 'env'>('env');
  const [msg, setMsg] = useState('');
  const [test, setTest] = useState<{ ok: boolean; error?: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.getDbConfig().then((r) => { setV((s) => ({ ...s, ...r.config, password: '' })); setSource(r.source); });
  }, []);

  const set = (k: string) => (e: ChangeEvent<HTMLInputElement>) => setV((s) => ({ ...s, [k]: e.target.value }));
  const body = () => ({ host: v.host, port: Number(v.port), database: v.database, user: v.user, password: v.password || undefined, socketPath: v.socketPath || undefined });

  const runTest = async () => {
    setTest(null); setBusy(true);
    try { setTest(await adminApi.testDbConfig(body())); }
    catch { setTest({ ok: false, error: 'Test isteği başarısız' }); }
    finally { setBusy(false); }
  };
  const save = async () => {
    setMsg(''); setBusy(true);
    try { await adminApi.saveDbConfig(body()); setMsg('Kaydedildi — değişiklik uygulama yeniden başlatılınca etkinleşir.'); }
    catch { setMsg('Kaydedilemedi'); }
    finally { setBusy(false); }
  };

  return (
    <Card title="Veritabanı (MySQL)" className="bank-card">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p className="pay-note">
          Bağlantı bilgileri {source === 'file' ? 'panelden (config/database.json)' : 'ortam değişkenlerinden'} okunuyor.
          Kaydedince <strong>config/database.json</strong>'a yazılır ve uygulama <strong>yeniden başlatılınca</strong> etkinleşir.
        </p>
        <div className="inline-form">
          <Field label="Host" id="db-host" value={v.host ?? ''} onChange={set('host')} placeholder="localhost" />
          <Field label="Port" id="db-port" type="number" value={v.port ?? 3306} onChange={set('port')} />
          <Field label="Veritabanı Adı" id="db-name" value={v.database ?? ''} onChange={set('database')} />
          <Field label="Kullanıcı" id="db-user" value={v.user ?? ''} onChange={set('user')} />
          <Field label={v.hasPassword ? 'Şifre (dolu — değiştirmek için yazın)' : 'Şifre'} id="db-pass" type="password" value={v.password ?? ''} onChange={set('password')} />
          <div className="full">
            <Field label="Socket yolu (opsiyonel — doluysa host/port yok sayılır)" id="db-socket" value={v.socketPath ?? ''} onChange={set('socketPath')} placeholder="/var/lib/mysql/mysql.sock" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="sm" variant="secondary" onClick={runTest} disabled={busy}>Bağlantıyı Test Et</Button>
          <Button size="sm" onClick={save} disabled={busy}>Kaydet</Button>
          {test && (
            <span style={{ fontSize: 13, color: test.ok ? 'var(--good)' : 'var(--bad)' }}>
              {test.ok ? '✓ Bağlantı başarılı' : `✕ ${test.error ?? 'Bağlantı başarısız'}`}
            </span>
          )}
          {msg && <span style={{ fontSize: 13, color: 'var(--good)' }}>{msg}</span>}
        </div>
      </div>
    </Card>
  );
}

function SmtpForm() {
  const [v, setV] = useState<Record<string, any>>({ enabled: false, host: '', port: 587, user: '', pass: '', from: '' });
  const [msg, setMsg] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [testing, setTesting] = useState(false);
  useEffect(() => { adminApi.getSetting('smtp').then((r) => setV({ ...v, ...r.value })); }, []); // eslint-disable-line
  const save = async () => { await adminApi.saveSetting('smtp', v); setMsg('Kaydedildi'); setTimeout(() => setMsg(''), 2000); };
  const set = (k: string) => (e: any) => setV((s) => ({ ...s, [k]: e.target.value }));
  const test = async () => {
    setTesting(true);
    setTestMsg(null);
    try {
      // Önce mevcut formu kaydet ki test kayıtlı ayarları kullansın.
      await adminApi.saveSetting('smtp', v);
      const r = await adminApi.testSmtp(testTo || undefined);
      if (r.ok) setTestMsg({ ok: true, text: `Test e-postası gönderildi → ${r.to}. Gelen kutunu (ve spam) kontrol et.` });
      else setTestMsg({ ok: false, text: `Gönderilemedi: ${r.error}` });
    } catch (e: any) {
      setTestMsg({ ok: false, text: e?.message ?? 'Test başarısız' });
    } finally {
      setTesting(false);
    }
  };
  return (
    <Card title="E-posta (SMTP)" className="bank-card">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Switch checked={!!v.enabled} onChange={(val) => setV((s) => ({ ...s, enabled: val }))} label="E-posta gönderimi aktif" />
        <div className="inline-form">
          <Field label="Host" id="h" value={v.host ?? ''} onChange={set('host')} />
          <Field label="Port" id="p" type="number" value={v.port ?? 587} onChange={set('port')} />
          <Field label="Kullanıcı" id="u" value={v.user ?? ''} onChange={set('user')} />
          <Field label={v.hasPass ? 'Şifre (dolu — değiştirmek için yazın)' : 'Şifre'} id="pw" type="password" value={v.pass ?? ''} onChange={set('pass')} />
          <Field label="Gönderen (from)" id="f" value={v.from ?? ''} onChange={set('from')} />
        </div>
        <Switch checked={!!v.allowInsecureTls}
          onChange={(val) => setV((s) => ({ ...s, allowInsecureTls: val }))}
          label="Güvensiz TLS'e izin ver (yalnız mail sunucusu sertifika hatası veriyorsa açın)" />
        <p className="pay-note">
          Güvenli varsayılan: sertifika doğrulaması <strong>açık</strong>. Test e-postası “sertifika/self-signed”
          hatası verirse bu kutuyu işaretleyip tekrar deneyin.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="sm" onClick={save}>Kaydet</Button>
          {msg && <span style={{ color: 'var(--good)', fontSize: 13 }}>{msg}</span>}
        </div>
        <div className="inline-form" style={{ alignItems: 'flex-end' }}>
          <Field label="Test e-postası adresi (boşsa kendi adresine)" id="testto" value={testTo} onChange={(e: any) => setTestTo(e.target.value)} />
          <Button size="sm" variant="secondary" onClick={test} disabled={testing}>{testing ? 'Gönderiliyor…' : 'Test Gönder'}</Button>
        </div>
        {testMsg && (
          <div style={{ fontSize: 13, color: testMsg.ok ? 'var(--good)' : 'var(--bad)', background: testMsg.ok ? 'var(--good-wash)' : 'var(--bad-wash)', border: '1px solid transparent', borderRadius: 8, padding: '9px 12px' }}>
            {testMsg.ok ? '✅ ' : '⚠️ '}{testMsg.text}
          </div>
        )}
      </div>
    </Card>
  );
}

/** Bildirim tercihleri — başarılı ödemede bayiye otomatik e-posta/SMS gönderimi (aç/kapa). */
function NotificationsForm() {
  const [v, setV] = useState<Record<string, any>>({ paymentSuccessEmail: true, paymentSuccessSms: true });
  const [msg, setMsg] = useState('');
  useEffect(() => { adminApi.getSetting('notifications').then((r) => setV((s) => ({ ...s, ...r.value }))); }, []); // eslint-disable-line
  const save = async () => { await adminApi.saveSetting('notifications', v); setMsg('Kaydedildi'); setTimeout(() => setMsg(''), 2000); };
  return (
    <Card title="Bildirimler" className="bank-card">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p className="pay-note">Başarılı bir ödeme (tahsilat) alındığında ilgili bayiye otomatik bildirim gönderimini buradan açıp kapatın.</p>
        <Switch checked={v.paymentSuccessEmail !== false}
          onChange={(val) => setV((s) => ({ ...s, paymentSuccessEmail: val }))}
          label="Başarılı ödemede bayiye e-posta (makbuz) gönder" />
        <Switch checked={v.paymentSuccessSms !== false}
          onChange={(val) => setV((s) => ({ ...s, paymentSuccessSms: val }))}
          label="Başarılı ödemede bayiye SMS gönder" />
        <div className="field">
          <label htmlFor="paymentNotifyEmails">Ödeme bildirimi yapılacak e-posta adresleri</label>
          <textarea id="paymentNotifyEmails" className="input" rows={2}
            placeholder="ornek@firma.com, muhasebe@firma.com"
            value={v.paymentNotifyEmails ?? ''}
            onChange={(e) => setV((s) => ({ ...s, paymentNotifyEmails: e.target.value }))} />
          <p className="pay-note" style={{ marginTop: 6 }}>Başarılı bir tahsilatta bayiye giden makbuz e-postasının aynısı bu adreslere de gönderilir. Birden fazla adresi virgülle ayırarak girin.</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Button size="sm" onClick={save}>Kaydet</Button>
          {msg && <span style={{ color: 'var(--good)', fontSize: 13 }}>{msg}</span>}
        </div>
      </div>
    </Card>
  );
}

function SmsForm() {
  const [v, setV] = useState<Record<string, any>>({ enabled: false, provider: 'log', sender: '' });
  const [msg, setMsg] = useState('');
  useEffect(() => { adminApi.getSetting('sms').then((r) => setV({ ...v, ...r.value })); }, []); // eslint-disable-line
  const save = async () => { await adminApi.saveSetting('sms', v); setMsg('Kaydedildi'); setTimeout(() => setMsg(''), 2000); };
  return (
    <Card title="SMS" className="bank-card">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Switch checked={!!v.enabled} onChange={(val) => setV((s) => ({ ...s, enabled: val }))} label="SMS gönderimi aktif" />
        <div className="inline-form">
          <div className="field">
            <label>Sağlayıcı</label>
            <select className="input" value={v.provider ?? 'log'} onChange={(e) => setV((s) => ({ ...s, provider: e.target.value }))}>
              <option value="log">Log (gönderme)</option>
              <option value="netgsm">NetGSM</option>
              <option value="iletimerkezi">İleti Merkezi</option>
            </select>
          </div>
          <Field label="Gönderen Başlık" id="snd" value={v.sender ?? ''} onChange={(e) => setV((s) => ({ ...s, sender: e.target.value }))} />
        </div>
        <p className="pay-note">Sağlayıcı seçilip kimlikler girildiğinde gerçek gönderim aktifleşir.</p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Button size="sm" onClick={save}>Kaydet</Button>
          {msg && <span style={{ color: 'var(--good)', fontSize: 13 }}>{msg}</span>}
        </div>
      </div>
    </Card>
  );
}

export function Settings() {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const load = () => adminApi.contracts().then((r) => setContracts(r.contracts));
  useEffect(() => { load(); }, []);

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-eyebrow">Ayarlar</div>
        <h1>Site Ayarları</h1>
      </div>

      <h2 style={{ fontSize: 17, margin: '8px 0 12px' }}>Genel Ayarlar</h2>
      <SiteForm />

      <h2 style={{ fontSize: 17, margin: '20px 0 12px' }}>Veritabanı Ayarları</h2>
      <DbSettingsForm />

      <h2 style={{ fontSize: 17, margin: '20px 0 12px' }}>Bildirimler</h2>
      <NotificationsForm />

      <h2 style={{ fontSize: 17, margin: '20px 0 12px' }}>Bildirim Kanalları</h2>
      <SmtpForm />
      <SmsForm />

      <h2 style={{ fontSize: 17, margin: '20px 0 12px' }}>Sözleşmeler</h2>
      {contracts.map((c) => <ContractEditor key={c.key} c={c} onSaved={load} />)}
    </div>
  );
}
